import React, { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import config from '../../../constants/config';
import '../styles/EventsCalendarDash.css';

export default function EventsCalendarDash() {
    const { account } = useOutletContext() || {};
    const [cookies] = useCookies(["account-canto"]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    const [formData, setFormData] = useState({
        title: '',
        start_date: '',
        end_date: '',
        type: 'conversation',
        location: '',
        description: '',
        image: ''
    });
    
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [selectedImagePreview, setSelectedImagePreview] = useState('');
    const [formErrors, setFormErrors] = useState({});
    
    useEffect(() => {
        fetchEvents();
    }, []);
    
    const fetchEvents = () => {
        setLoading(true);
        
        axios.post(config.api.url + 'events_calendar/fetch', {}, {
            crossdomain: true,
            headers: {
                'api': config.api.key
            }
        })
        .then((response) => {
            const { success, data } = response.data;
            if (success) {
                setEvents(data);
            } else {
                console.error('Error fetching events:', response.data.response);
            }
            setLoading(false);
        })
        .catch((error) => {
            console.error("Fetch error:", error);
            setLoading(false);
        });
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };
    
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImageFile(file);
            const imageUrl = URL.createObjectURL(file);
            setSelectedImagePreview(imageUrl);
        }
    };
    
    const validateForm = () => {
        const errors = {};
        
        if (!formData.title.trim()) {
            errors.title = 'Title is required';
        }
        
        if (!formData.start_date) {
            errors.start_date = 'Start date is required';
        }
        
        if (!formData.end_date) {
            errors.end_date = 'End date is required';
        }
        
        if (formData.start_date && formData.end_date) {
            const startTimestamp = Math.floor(new Date(formData.start_date).getTime() / 1000);
            const endTimestamp = Math.floor(new Date(formData.end_date).getTime() / 1000);
            
            if (endTimestamp < startTimestamp) {
                errors.end_date = 'End date must be after or equal to start date';
            }
        }
        
        if (!formData.location.trim()) {
            errors.location = 'Location is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('start_date', Math.floor(new Date(formData.start_date).getTime() / 1000));
        formDataToSend.append('end_date', Math.floor(new Date(formData.end_date).getTime() / 1000));
        formDataToSend.append('type', formData.type);
        formDataToSend.append('location', formData.location);
        formDataToSend.append('description', formData.description || '');
        
        if (selectedImageFile) {
            formDataToSend.append('image', selectedImageFile);
        } else if (formData.image) {
            formDataToSend.append('image_url', formData.image);
        }
        
        const endpoint = editingEvent ? 'events_calendar/update' : 'events_calendar/create';
        
        if (editingEvent) {
            formDataToSend.append('id', editingEvent.id);
        }
        
        setLoading(true);
        
        axios.post(config.api.url + endpoint, formDataToSend, {
            crossdomain: true,
            headers: {
                'api': config.api.key,
                'auth': account?.auth_token || cookies['account-canto'],
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((response) => {
            const { success, response: message, errors } = response.data;
            if (success) {
                alert(message);
                fetchEvents();
                resetForm();
            } else {
                if (errors) {
                    setFormErrors(errors);
                } else {
                    alert('Error: ' + message);
                }
            }
            setLoading(false);
        })
        .catch((error) => {
            console.error("Submit error:", error);
            alert('Error submitting event');
            setLoading(false);
        });
    };
    
    const handleEdit = (event) => {
        setEditingEvent(event);
        setFormData({
            title: event.title,
            start_date: new Date(event.start_date * 1000).toISOString().split('T')[0],
            end_date: new Date(event.end_date * 1000).toISOString().split('T')[0],
            type: event.type,
            location: event.location,
            description: event.description || '',
            image: event.image || ''
        });
        setSelectedImageFile(null);
        setSelectedImagePreview(event.image || '');
        setShowForm(true);
    };
    
    const handleDelete = (eventId) => {
        if (!window.confirm('Are you sure you want to delete this event?')) {
            return;
        }
        
        setLoading(true);
        
        axios.post(config.api.url + 'events_calendar/delete', { id: eventId }, {
            crossdomain: true,
            headers: {
                'api': config.api.key,
                'auth': account?.auth_token || cookies['account-canto']
            }
        })
        .then((response) => {
            const { success, response: message } = response.data;
            if (success) {
                alert(message);
                fetchEvents();
            } else {
                alert('Error: ' + message);
            }
            setLoading(false);
        })
        .catch((error) => {
            console.error("Delete error:", error);
            alert('Error deleting event');
            setLoading(false);
        });
    };
    
    const resetForm = () => {
        setFormData({
            title: '',
            start_date: '',
            end_date: '',
            type: 'conversation',
            location: '',
            description: '',
            image: ''
        });
        setSelectedImageFile(null);
        setSelectedImagePreview('');
        setFormErrors({});
        setEditingEvent(null);
        setShowForm(false);
    };
    
    const filteredEvents = events.filter(event => {
        const matchesType = filterType === 'all' || event.type === filterType;
        const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            event.location.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesType && matchesSearch;
    });
    
    const formatDateRange = (startTimestamp, endTimestamp) => {
        const start = new Date(startTimestamp * 1000);
        const end = new Date(endTimestamp * 1000);
        
        if (start.toDateString() === end.toDateString()) {
            return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
        
        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    };
    
    return (
        <div className="events-calendar-dash">
            <div className="dash-header">
                <h1>Events Calendar Manager</h1>
                <button 
                    className="btn-primary"
                    onClick={() => setShowForm(!showForm)}
                >
                    {showForm ? 'Cancel' : '+ Add New Event'}
                </button>
            </div>
            
            {showForm && (
                <div className="event-form-container">
                    <h2>{editingEvent ? 'Edit Event' : 'Create New Event'}</h2>
                    <form onSubmit={handleSubmit} className="event-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="Event title"
                                    maxLength={255}
                                />
                                {formErrors.title && <span className="error">{formErrors.title}</span>}
                            </div>
                            
                            <div className="form-group">
                                <label>Event Type *</label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                >
                                    <option value="conversation">CANTO Conversations</option>
                                    <option value="connect">CANTO Connect</option>
                                    <option value="conference">CANTO Conference</option>
                                    <option value="industry">Industry Events</option>
                                </select>
                                {formErrors.type && <span className="error">{formErrors.type}</span>}
                            </div>
                        </div>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label>Start Date *</label>
                                <input
                                    type="date"
                                    name="start_date"
                                    value={formData.start_date}
                                    onChange={handleInputChange}
                                />
                                {formErrors.start_date && <span className="error">{formErrors.start_date}</span>}
                            </div>
                            
                            <div className="form-group">
                                <label>End Date *</label>
                                <input
                                    type="date"
                                    name="end_date"
                                    value={formData.end_date}
                                    onChange={handleInputChange}
                                    min={formData.start_date}
                                />
                                {formErrors.end_date && <span className="error">{formErrors.end_date}</span>}
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label>Location *</label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                placeholder="Event location"
                                maxLength={255}
                            />
                            {formErrors.location && <span className="error">{formErrors.location}</span>}
                        </div>
                        
                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Event description"
                                rows={4}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Event Image</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="file-input"
                            />
                            {selectedImagePreview && (
                                <div className="image-preview">
                                    <img src={selectedImagePreview} alt="Preview" />
                                </div>
                            )}
                        </div>
                        
                        <div className="form-actions">
                            <button type="submit" className="btn-primary" disabled={loading}>
                                {loading ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                            </button>
                            <button type="button" className="btn-secondary" onClick={resetForm}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            <div className="events-filters">
                <input
                    type="text"
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                />
                
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Types</option>
                    <option value="conversation">CANTO Conversations</option>
                    <option value="connect">CANTO Connect</option>
                    <option value="conference">CANTO Conference</option>
                    <option value="industry">Industry Events</option>
                </select>
            </div>
            
            <div className="events-table">
                {loading ? (
                    <div className="loading">Loading events...</div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Type</th>
                                <th>Date Range</th>
                                <th>Location</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEvents.length > 0 ? (
                                filteredEvents.map(event => (
                                    <tr key={event.id}>
                                        <td>{event.title}</td>
                                        <td>
                                            <span className={`badge badge-${event.type}`}>
                                                {event.type}
                                            </span>
                                        </td>
                                        <td>{formatDateRange(event.start_date, event.end_date)}</td>
                                        <td>{event.location}</td>
                                        <td className="actions">
                                            <button
                                                className="btn-edit"
                                                onClick={() => handleEdit(event)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn-delete"
                                                onClick={() => handleDelete(event.id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="no-data">
                                        No events found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
